import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EmployeeSignatureDto } from '@ups/xplat/api/dto';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { Observable } from 'rxjs';

/**
 * Reads and upserts an attendee's single reusable saved signature for the HSE
 * training sign-in sheet, keyed by the scanned employee's HRRef. Fronts the
 * Sparta API `EmployeeSignatureController` (`api/spartaemployee/signature/{hrRef}`,
 * WI 31501 slice 1). Kept separate from `ForemanJobService.getUserSignature`,
 * whose store is keyed by the logged-in user, not an arbitrary attendee.
 */
@Injectable({
  providedIn: 'root',
})
export class EmployeeSignatureService {
  private readonly httpFactory = inject(MyHttpClientFactory);
  private readonly http: HttpClient;

  constructor() {
    const url = environment.urls.spartaAPI;
    this.http = this.httpFactory.createHttpClient(
      url,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  /** The scanned employee's saved signature, or null if none is stored. */
  getEmployeeSignature(hrRef: number): Observable<EmployeeSignatureDto | null> {
    return this.http.get(
      `api/spartaemployee/signature/${hrRef}`
    ) as Observable<EmployeeSignatureDto | null>;
  }

  /**
   * Upsert the scanned employee's saved signature (bare base64, no data-URI
   * prefix). The offline mutation queue in MyHttpClientFactory replays this when
   * the device is offline at save time.
   */
  saveEmployeeSignature(
    hrRef: number,
    dto: EmployeeSignatureDto
  ): Observable<void> {
    return this.http.put(
      `api/spartaemployee/signature/${hrRef}`,
      dto
    ) as unknown as Observable<void>;
  }
}
